import { reaction } from 'mobx';
import { types as t, flow } from 'mobx-state-tree';

import { withEnv } from 'models/withEnv';

const GetAcceptTemplateField = t
  .model('GetAcceptTemplateField', {
    name: t.identifier,
    type: t.enumeration('GetAcceptTemplateFieldType', [
      'choices',
      'string',
      'date',
      'text',
      'boolean',
    ]),
    label: t.string,
    value: t.string,
    choices: t.frozen(),
    required: t.boolean,
    initialValue: t.frozen(),
  })
  .actions((self) => ({
    setValue(value) {
      self.value = value;
    },
  }));

const GetAcceptTemplate = t.model('GetAcceptTemplate', {
  id: t.identifier,
  name: t.string,
});

export const GetAcceptSettings = t
  .model('GetAcceptSettings', {
    isAuthed: false,
    needsApproval: false,
    needsApprovalLabel: t.maybeNull(t.string),
    needsApprovalError: false,
    code: t.maybeNull(t.string),
    checkingAuth: false,
    templates: t.array(GetAcceptTemplate),
    loadingTemplates: false,
    templateId: t.maybeNull(t.string),
    fields: t.array(GetAcceptTemplateField),
    loadingFields: false,
    loadedFields: false,
    submitting: false,
  })
  .views(withEnv)
  .actions((self) => ({
    afterCreate() {
      reaction(
        () => self.isAuthed,
        (authed) => {
          if (authed) {
            self.getTemplates();
          }
        },
      );
      self.getSettings();
    },
    toggleNeedsApproval() {
      self.needsApproval = !self.needsApproval;
    },
    setNeedsApprovalLabel(val) {
      self.needsApprovalLabel = val;
    },
    getSettings: flow(function* () {
      const res = yield self.env.api.integrations.getacceptSettings();
      if (res.status === 200) {
        self.isAuthed = true;
        self.needsApproval = res.data.needsApproval;
        self.needsApprovalLabel = res.data.approvalFieldLabel;
      }
    }),
    saveSettings: flow(function* () {
      self.subbmitting = true;
      const data = {
        needsApproval: self.needsApproval,
        needsApprovalLabel: self.needsApprovalLabel,
      };
      const res = yield self.env.api.integrations.getacceptSaveSettings(data);
      if (res.status === 400) {
        self.needsApprovalError = true;
      } else {
        self.needsApprovalError = false;
        self.env.showSuccess(self.env.t('integrations.getaccept.settings.save.success'));
      }
      self.subbmitting = false;
    }),
    setCode: flow(function* (redirectUrl, code) {
      self.code = code;
      self.checkingAuth = true;
      const res = yield self.env.api.integrations.getacceptAuth(redirectUrl, code);
      if (res.status === 200) {
        self.getSettings();
        self.checkingAuth = false;
      }
    }),
    submitTemplate: flow(function* () {
      self.submitting = true;
      let values = {};
      self.fields.forEach((field) => {
        values[field.name] = field.value;
      });
      const res = yield self.env.api.integrations.getacceptSubmitTemplate(self.templateId, values);
      if (res.status === 200) {
        self.env.showSuccess(self.env.t('integrations.getaccept.settings.template.save.success'));
      } else {
        self.env.showError(self.env.t('integrations.getaccept.settings.template.save.error'));
      }
      self.submitting = false;
      return res;
    }),
    getTemplates: flow(function* () {
      self.loadingTemplates = true;
      const res = yield self.env.api.integrations.getacceptTemplates();
      if (!res.error) {
        self.templates = res.data;
      }
      self.loadingTemplates = false;
    }),
    setTemplateId(templateId) {
      self.templateId = templateId;
      self.getFields(templateId);
    },
    getFields: flow(function* (templateId) {
      self.loadingFields = true;
      const res = yield self.env.api.integrations.getacceptFields(templateId);
      if (!res.error) {
        self.fields = res.data;
      }
      self.loadingFields = false;
      self.loadedFields = true;
    }),
  }));
